<template>
  <div>
    <v-card color="transparent" elevation="0">
      <v-data-table
        :footer-props="footerProps"
        :headers="headerList"
        :items="filteredTestPlanList"
        :loading="loading"
        :options.sync="tableOptions"
        :server-items-length="isGlobalSearch ? searchTotalCount : undefined"
        :single-select="false"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @update:options="handlePagination"
        class="och-testPlanTable elevation-0 mt-3"
        data-cy="listTable"
        fixed-header
        height="53vh"
        item-key="releaseNoteId"
        no-data-text="No Release Notes match the current filter."
        v-if="hasTestPlanListView"
      >
        <template v-slot:header.batchEdit v-if="!isGlobalSearch">
          <v-checkbox
            :disabled="!testPlanList || !testPlanList.length"
            :indeterminate="isIndeterminate"
            :value="isAllSelected"
            @change="selectAll($event)"
            class="mt-2"
            data-cy="testPlanTable__selectAll"
          ></v-checkbox>
        </template>
        <template v-slot:item="{ item }">
          <tr data-cy="testPlanTable__row" :class="getRowStyles(item)">
            <template v-if="!isGlobalSearch">
              <td>
                <v-checkbox
                  :data-cy="`testPlanTable-select-${item.releaseNoteId}`"
                  :value="!!testPlanSelectionListLookup[item.releaseNoteId]"
                  @change="select(item, $event)"
                ></v-checkbox>
              </td>
            </template>
            <template v-if="isGlobalSearch">
              <td v-if="isGlobalSearch">{{ item.packTitle }} ({{ item.packId }})</td>
            </template>
            <template v-else>
              <td class="och-testPlanTable__cell--assignedTo">
                <inline-user-picker
                  :id="item.releaseNoteId"
                  :username="item.assignedTo"
                  @updateInlineUserPicker="updateInlineUserPicker"
                  :disabled="!hasTestPlanEdit"
                  data-cy="testPlanList__userPicker"
                ></inline-user-picker>
              </td>
              <td>{{ item.datePublished }}</td>
              <td data-cy="testPlanStatus">{{ item.status || "--" }}</td>
            </template>
            <td>
              <a :href="`https://jira.ochin.org/browse/${item.uatJira}`" target="_blank">
                <span>{{ item.uatJira }}</span>
              </a>
            </td>
            <td class="och-testPlanTable__cell--title">
              <div
                v-if="isGlobalSearch && (item.status === 'Not Impacted' || item.status === 'Historically Not Impacted')"
              >
                <v-icon class="mr-1 mb-1" color="grey" x-small>fas fa-exclamation-triangle</v-icon>{{ item.status }}
              </div>
              <router-link
                :to="
                  `/pack/${item.packId}/releaseNote/${item.releaseNoteId}/testPlan/${serviceAreaId}/view?${
                    isGlobalSearch ? 'globalSearch=1' : ''
                  }`
                "
                >{{ item.title }}
              </router-link>
            </td>
            <td>
              <v-tooltip v-if="item.summary" bottom max-width="420">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="och-testPlanTable__cell--summary"
                    v-bind="attrs"
                    v-on="on"
                  >{{ ellipsis(stripHtmlTags(item.summary)) }}</div>
                </template>
                <span v-html="item.summary"></span>
              </v-tooltip>
            </td>
            <td>{{ item.operationalizeType || "--" }}</td>
            <td>{{ formatArray(item.userRole) || "--" }}</td>
            <td>{{ item.application || "--" }}</td>
            <td>{{ item.primaryActivity || "--" }}</td>
            <td>{{ formatServiceAreaAttributeList(item.serviceAreaAttributeList) || "--" }}</td>
            <td>{{ item.releaseType || "--" }}</td>
            <td>
              <v-tooltip bottom max-width="420" v-if="item.additionalComments">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="och-testPlanTable__cell--summary"
                    v-bind="attrs"
                    v-on="on"
                  >{{ ellipsis(stripHtmlTags(item.additionalComments)) }}</div>
                </template>
                <span v-html="item.additionalComments"></span>
              </v-tooltip>
              <span v-else>--</span>
            </td>
          </tr>
        </template>
        <v-alert slot="no-results" color="error" icon="warning"
          >Your search for "{{ quickSearchTerms }}" found no results.</v-alert
        >
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import { cloneDeep, keyBy } from "lodash";
import { truncate, stripHtmlTags } from "@/lib/summaryFormatters";

import InlineUserPicker from "@/components/inlineUserPicker";

import { formatArray } from "@/lib/formatters";

export default {
  name: "testPlanListComponent",
  components: {
    InlineUserPicker,
  },
  computed: {
    filteredTestPlanList() {
      if (!this.quickSearchTerms || this.isGlobalSearch) {
        return this.testPlanListMapped;
      }
      const filteredList = this.testPlanListMapped.filter(item => {
        const searchTerms = this.quickSearchTerms.toLowerCase().split(" ");
        const searchableFields = this.headers.map(header => header.value);

        return searchableFields.some(field => {
          let fieldValue;
          if (field === "serviceAreaAttributeList") {
            fieldValue = item[field]
              ? this.formatServiceAreaAttributeList(item[field])
                  .toString()
                  .toLowerCase()
              : "";
          } else {
            fieldValue = item[field] ? item[field].toString().toLowerCase() : "";
          }
          return searchTerms.every(term => fieldValue.includes(term));
        });
      });
      return filteredList;
    },
    headerList() {
      const { globalHeaders, isGlobalSearch, headers, localHeaders } = this;

      if (isGlobalSearch) {
        return globalHeaders.concat(headers);
      } else {
        return localHeaders.concat(headers);
      }
    },
    paginationPage() {
      return parseInt(this.$route.query.page, 10) || 1;
    },
    rowsPerPage() {
      return this.$store.state.session.user.userConfiguration.widgets.testPlanList.rowsPerPage;
    },
    serviceArea() {
      return this.$store.state.session.serviceArea;
    },
    tableOptions: {
      get() {
        return this.options;
      },
      set(val) {
        this.updateOptions(val);
      },
    },
    testPlanListMapped() {
      const testPlanList = cloneDeep(this.testPlanList);
      return testPlanList.map(testPlan => {
        testPlan.title = `${testPlan.title} [${testPlan.releaseNoteId}]`;
        return testPlan;
      });
    },
    testPlanSelectionListLookup() {
      return keyBy(this.testPlanSelectionList, "releaseNoteId");
    },
    userConfig() {
      return this.$store.state.session.user.userConfiguration;
    },
    ...mapGetters({
      getServiceAreaAttribute: "admin/serviceAreaAttribute/getById",
      hasTestPlanListView: "session/hasTestPlanListView",
      hasTestPlanEdit: "session/hasTestPlanEdit",
    }),
    ...mapState({
      serviceAreaAttributeList: state => state.admin.serviceAreaAttribute.list,
      serviceAreaId: state => state.session.serviceArea,
      searchTotalCount: state => state.releaseNote.searchTotalCount,
      testPlanSelectionList: state => state.releaseNote.testPlanBatchEditSelectionList,
    }),
  },
  mounted() {
    this.sortBy = "productionDate";
    this.sortDesc = true;
    // The store needs to be reset so that styles reset properly when navigating back to this view.
    this.updateTestPlanBatchEditSelectionList([]);
  },
  data() {
    return {
      personPickerStatus: {},
      footerProps: {
        "items-per-page-options": [5, 10, 25, 50],
      },
      formatArray,
      globalHeaders: [
        {
          text: "Pack",
          value: "packId",
          width: "150",
        },
      ],
      isAllSelected: false,
      isIndeterminate: false,
      localHeaders: [
        {
          text: "",
          value: "batchEdit",
          sortable: false,
        },
        {
          text: "Assigned To",
          value: "assignedTo",
        },
        {
          text: "Date Published",
          value: "datePublished",
          width: "130",
        },
        {
          text: "Status",
          value: "status",
          width: "110",
        },
      ],
      headers: [
        {
          text: "UAT JIRA",
          value: "uatJira",
        },
        {
          text: "Title",
          value: "title",
        },
        {
          text: "Summary",
          value: "summary",
        },
        {
          text: "Operationalize Type",
          value: "operationalizeType",
        },
        {
          text: "User Role",
          value: "userRole",
        },
        {
          text: "Application",
          value: "application",
        },
        {
          text: "Primary Activity",
          value: "primaryActivity",
          width: "150",
        },
        {
          text: "Impacts",
          value: "serviceAreaAttributeList",
          width: "150",
        },
        {
          text: "Release Type",
          value: "releaseType",
        },
        {
          text: "Comments",
          value: "additionalComments",
        },
      ],
      sortBy: [],
      sortDesc: null,
    };
  },
  methods: {
    ellipsis(text) {
      return truncate(text, 200);
    },
    formatServiceAreaAttributeList(attributeList) {
      if (attributeList && this.serviceAreaAttributeList.length) {
        const attributeNameList = attributeList.map(attributeId => {
          const attribute = this.getServiceAreaAttribute(attributeId);
          return attribute.name;
        });
        return attributeNameList.join(", ");
      }
    },
    getTestPlanList() {
      this.$emit("refreshList");
    },
    getRowStyles(item) {
      let defaultStyles = "och-testPlanTable__row";

      if (
        this.testPlanSelectionList?.length &&
        this.testPlanSelectionListLookup[item.releaseNoteId] &&
        !this.isGlobalSearch
      ) {
        defaultStyles = defaultStyles.concat(" och-testPlanTable__row--highlighted");
        return defaultStyles;
      }
      if (this.isGlobalSearch && (item.status === "Not Impacted" || item.status === "Historically Not Impacted")) {
        defaultStyles = defaultStyles.concat(" font-italic grey--text text--darken-2");
        return defaultStyles;
      }
      return defaultStyles;
    },
    handlePagination(val) {
      this.$emit("paginate", val);
    },
    navigateToPrint(testPlanList) {
      this.$router.push({
        name: "testPlanPrint",
        params: { testPlanList },
      });
    },
    select(item, value) {
      const { releaseNoteId } = item;
      if (!releaseNoteId) {
        return;
      }
      let selectionList = cloneDeep(this.testPlanSelectionList);
      if (value) {
        selectionList.push(item);
      } else {
        selectionList = this.testPlanSelectionList.filter(testPlan => testPlan.releaseNoteId !== releaseNoteId);
      }
      if (selectionList.length > 0) {
        this.isIndeterminate = true;
        this.isAllSelected = false;
      }
      if (selectionList.length === 0) this.isIndeterminate = false;
      this.updateTestPlanBatchEditSelectionList(selectionList);
    },
    selectAll(value) {
      if (value) {
        this.updateTestPlanBatchEditSelectionList(cloneDeep(this.filteredTestPlanList));
        this.isAllSelected = true;
      } else {
        this.updateTestPlanBatchEditSelectionList([]);
        this.isAllSelected = false;
      }
      this.isIndeterminate = false;
    },
    stripHtmlTags,
    async updateInlineUserPicker({ id, username = null }) {
      await this.$store.dispatch("releaseNote/updateAssignTo", {
        assignedTo: username,
        releaseNoteId: id,
        serviceArea: this.serviceAreaId,
      });
      await this.getTestPlanList(this.packId);
    },
    updateOptions(val) {
      this.$emit("updateOptions", val);
    },
    ...mapMutations({
      updateTestPlanBatchEditSelectionList: "releaseNote/setTestPlanBatchEditSelectionList",
    }),
  },
  props: {
    isGlobalSearch: {
      default: false,
      type: Boolean,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      required: true,
    },
    quickSearchTerms: {
      type: String,
      default: "",
    },
    testPlanList: {
      required: true,
      type: Array,
    },
  },
  watch: {
    testPlanList() {
      if (!this.testPlanList || this.testPlanList.length === 0) {
        this.isAllSelected = false;
        this.isIndeterminate = false;
      }
    },
    testPlanSelectionList() {
      if (!this.testPlanSelectionList || this.testPlanSelectionList.length === 0) {
        this.isAllSelected = false;
        this.isIndeterminate = false;
      }
    },
  },
};
</script>

<style>
/* Styles are intentionally not scoped using the scoped keyword due to a strange bug
that results in the first table row not highlighting when selected if styles are scoped. */

.och-testPlanTable__cell--summary {
  max-width: 20rem;
  min-width: 18rem;
  overflow: hidden;
}

.och-testPlanTable__cell--title {
  min-width: 15rem;
}

.och-testPlanTable__cell--assignedTo {
  min-width: 15rem;
}

.och-testPlanTable__row > td {
  border: 1px dotted whitesmoke;
  border-top: none;
  padding: 0.1rem 0.5rem !important;
}

.och-testPlanTable__row--highlighted {
  background-color: #ffffbf;
}

.och-testPlanTable__row--highlighted:hover {
  background-color: #ebebb7 !important;
}

.och-testPlanTable thead th {
  padding: 0.1rem 0.5rem !important;
  white-space: normal !important;
  vertical-align: top !important;
}
</style>
